import { Avatar, Box, Checkbox, Typography, Link, Button, Skeleton } from "@mui/material";
import { UserInfo } from "../../types";
import { getUserData } from "../../utils/userDataFormatter";
import { ChatIcon, FollowIcon, VerificationIcon } from "../../assets/icons/MUI/NetworkIcons";
import { colors } from "../../theme/palette";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useInvalidateMyQuery } from "../../hooks/useInvalidateMyQuery";
import NewChatPopUp from "../../shared/NewChatPopUp";
import { followEntity, followUser } from "../../api/network";
import { useTranslation } from "react-i18next";
import { useDataFormatter } from "../../hooks/useDataFormatter";
import { useProfileData } from "../../hooks/useProfileData";

const UserItem = ({
  roleTab,
  user,
  compareData,
  checkCompareSelection,
}: {
  roleTab: number;
  user: UserInfo;
  compareData: any;
  checkCompareSelection: (value: any) => void;
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();

  const { data: userOwnData } = useProfileData();
  const {
    id,
    fullName,
    firstTwoChr,
    countryName,
    isVerified,
    media,
    role,
    isFollowing,
    profileLink,
    mutualFollowersCount,
    friendsCount,
    entityId,
  } = useDataFormatter(user);

  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    setIsDisabled(!compareData.some((el: any) => el.id === id) && compareData.length === 4);
  }, [compareData]);

  const [isFollowingState, setIsFollowingState] = useState<boolean>(isFollowing || false);
  const [openCreateMessage, setOpenCreateMessage] = useState<boolean>(false);

  const invalidateNetworkFollowersList = useInvalidateMyQuery("NetworkList");
  const IS_ENTITY = user.isEntity;

  return (
    <Box
      sx={{
        display: "flex",
        columnGap: "11px",
        alignItems: "flex-start",
        paddingBottom: "20px",
        borderBottom: "1px solid #EAECF0",
      }}
    >
      {roleTab !== 2 && (
        <Checkbox
          size="small"
          onClick={() => checkCompareSelection(user)}
          disabled={isDisabled}
          checked={compareData.some((el: any) => el.id === id)}
          sx={{ display: { xs: "none", md: "block" } }}
        />
      )}
      <Box
        sx={{
          width: "100%",
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          columnGap: "20px",
        }}
      >
        <Avatar style={{ width: "72px", height: "72px" }} src={media}>
          {firstTwoChr}
        </Avatar>
        {isVerified && (
          <VerificationIcon
            sx={{
              position: "absolute",
              top: "61px",
              left: "26px",
              zIndex: "10",
            }}
          />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Link
            sx={{
              textDecoration: "none",
              color: colors.dark1000,
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: "normal",
              maxWidth: "95%",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={() => navigate(profileLink)}
          >
            {fullName}
          </Link>
          <Typography variant="body14Regular" sx={{ color: colors.Dark1000 }}>
            {role}
          </Typography>
          <Typography variant="body14Regular" sx={{ color: colors.Dark600 }}>
            {mutualFollowersCount
              ? `${mutualFollowersCount} ${t("pages.goStar.mutualFollowers")}`
              : `0 ${t("pages.goStar.mutualFollowers")}`}
            {!entityId &&
              (friendsCount
                ? ` . ${t("pages.goStar.friendWith")} ${friendsCount}`
                : ` . ${t("pages.goStar.friendWith")} 0`)}
          </Typography>
          <Typography
            variant="body14Regular"
            sx={{
              color: colors.Dark600,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
            title={countryName}
          >
            {countryName}
          </Typography>
        </Box>
        {userOwnData.entityId !== entityId &&
          (isFollowingState ? (
            <>
              <Button
                variant="secondary-sm"
                fullWidth
                startIcon={<ChatIcon />}
                sx={{
                  marginTop: "auto",
                  width: "fit-content",
                }}
                onClick={() => setOpenCreateMessage(true)}
              >
                {t("pages.goStar.chat")}
              </Button>
              {openCreateMessage && (
                <NewChatPopUp
                  open={openCreateMessage}
                  handleClose={() => setOpenCreateMessage(false)}
                  receiverInfo={user}
                />
              )}
            </>
          ) : (
            <Button
              variant="outlined-sm"
              fullWidth
              startIcon={<FollowIcon />}
              onClick={async () => {
                let success;
                if (IS_ENTITY) {
                  success = await followEntity(entityId);
                } else {
                  success = await followUser(user.id);
                }
                if (success) {
                  invalidateNetworkFollowersList();
                  setIsFollowingState(true);
                }
              }}
              sx={{
                marginTop: "auto",
                width: "fit-content",
              }}
            >
              {t("pages.goStar.follow")}
            </Button>
          ))}
      </Box>
    </Box>
  );
};

export default UserItem;

export const UserLoadingItem = ({ isLast }: { isLast: Boolean }) => (
  <Box
    sx={{
      display: "flex",
      columnGap: "11px",
      alignItems: "flex-start",
      paddingBottom: isLast ? "" : "20px",
      borderBottom: isLast ? "unset" : "1px solid #EAECF0",
    }}
  >
    {/* Checkbox Skeleton */}
    <Skeleton variant="rectangular" width={20} height={20} />

    {/* Main Content */}
    <Box
      sx={{
        width: "100%",
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        columnGap: "20px",
      }}
    >
      {/* Avatar Skeleton */}
      <Skeleton variant="circular" width={72} height={72} />

      {/* Content Area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "calc(100% - 223px)",
        }}
      >
        {/* Name Link Skeleton */}
        <Skeleton width="20%" height={24} />

        {/* Role Skeleton */}
        <Skeleton width="45%" height={20} sx={{ mt: 1 }} />

        {/* Mutual Followers and Friends Skeleton */}
        <Skeleton width="75%" height={16} sx={{ mt: 1 }} />

        {/* Country Name Skeleton */}
        <Skeleton width="35%" height={16} sx={{ mt: 1 }} />
      </Box>

      {/* Button Skeletons */}
      <Skeleton
        variant="rectangular"
        sx={{
          width: "100px",
          height: "36px",
          borderRadius: "50px",
        }}
      />
    </Box>
  </Box>
);
